<script setup>
import { Head, Link } from '@inertiajs/vue3';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import { defineProps, nextTick, ref } from 'vue';
import Footer from '@/Components/Footer.vue';
import PublicLayout from '@/Layouts/PublicLayout.vue';

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
});

const prices = {
    petrol: 144,
    electricity: 28.7,
}

let example = {
    period: 36,
    mileage_annual: 12500,
    methods: [
        {
            name: 'Petrol + Carbon Removal',
            vehicle: '2024 BMW 3 Series 320i',
            lease: 651.10,
            wltp_100km: 6.5,
            storage: 59,
            fuel_unit: 'litres',
            fuel_unit_singular: 'litre',
            fuel_name: 'petrol',
            fuel_price: prices.petrol,
            co2_removal_per_tank: 20.55,
        },
        {
            name: 'Electric',
            vehicle: '2024 Tesla Model 3',
            lease: 784.57,
            wltp_100km: 13.2,
            storage: 60,
            fuel_unit: 'kWh',
            fuel_unit_singular: 'kWh',
            fuel_name: 'electricity',
            fuel_price: prices.electricity,
            co2_removal_per_tank: 0,
        }
    ]
}

example.period_mileage = example.mileage_annual * example.period / 12;

for (let method of example.methods) {
    method.range = {},
    method.range.range_km = 100 / method.wltp_100km * method.storage
    method.range.range_miles = method.range.range_km * 0.621371
    
    method.fuelling = {
        price_per_fuelling: method.storage * method.fuel_price / 100,
        fuellings_period: example.period_mileage / method.range.range_miles,
        fuel_used_period: example.period_mileage / method.range.range_miles * method.storage,
    }
    method.totals = {
        lease_total: method.lease * example.period,
        fuel_total: method.fuelling.price_per_fuelling * method.fuelling.fuellings_period,
        co2_removal_total: method.co2_removal_per_tank * method.fuelling.fuellings_period,
    }
    method.totals.total = method.totals.lease_total + method.totals.fuel_total + method.totals.co2_removal_total;
}

function formatCurrency(value) {
    const currencyFormat = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    return currencyFormat.format(value);
}

function formatNumber(value, round = 2) {
    const numberFormat = new Intl.NumberFormat('en-GB');
    value = value.toFixed(round);
    return numberFormat.format(value);
}

</script>


<template>
    <Head title="CO2 removal vs Electric" />

    <PublicLayout :route="route" :canLogin="canLogin">    
        <div class="text-neutral-800 mt-20 h-16 sm:h-24 w-full relative">
            <h1 class="text-2xl sm:text-5xl inline-block duration-500 absolute left-1/2 -translate-x-1/2 top-0 text-center h-full text-nowrap">
                CO<sub>2</sub> Removal vs Electric
            </h1>
        </div>
        
        <div class="mt-16 px-4 sm:px-8 max-w-5xl mx-auto text-center">
            <div class="text-2xl">How does carbon removal compare to running an electric vehicle?</div>
            <div class="mt-4 text-neutral-600 max-w-3xl mx-auto">
                Turncarbon has calculated the cost of running a new petrol vehicle with carbon removal, and a new electric vehicle over a <b>3 year period</b>, travelling <b>{{ formatNumber(example.mileage_annual) }} miles</b> per year.
            </div>

            <div class="mt-12 grid grid-cols-2 gap-x-2 gap-y-4 text-lg max-w-2xl mx-auto bg-white px-4 py-6 rounded-lg shadow-lg">
                <div v-for="method in example.methods">
                    <span class="text-sm sm:text-2xl font-semibold">
                        {{ method.vehicle }}
                    </span>
                    
                    <span class="text-sm sm:text-lg text-neutral-600 block">
                        {{ method.name }}
                    </span>
                
                    <span class="text-xs sm:text-sm text-neutral-600 block">
                        Range: {{ method.range.range_miles.toFixed(0) }} miles
                    </span>
                </div>

                <div v-for="method in example.methods" class="text-neutral-600 text-xl sm:text-3xl">
                    <span class="text-sm uppercase tracking-widest opacity-70">3-year total</span><br />
                    {{ formatCurrency(method.totals.total) }}
                </div>

                <div v-for="method in example.methods" class="text-sm sm:text-lg  text-neutral-600">
                    <span class="text-sm uppercase tracking-widest mb-3 opacity-70">Lease</span><br />
                    {{ formatCurrency(method.totals.lease_total) }}
                    <span class="text-sm block text-neutral-500">{{ formatCurrency(method.lease) }} per month</span>
                </div>

                <div v-for="method in example.methods" class="text-sm sm:text-lg text-neutral-600">
                    <span class="text-sm uppercase tracking-widest mb-3 opacity-70">{{ method.fuel_name }}</span><br />
                    {{ formatCurrency(method.totals.fuel_total) }} ({{ formatNumber(method.fuelling.fuel_used_period, 0) }} {{ method.fuel_unit }})
                </div>

                <div v-for="method in example.methods" class="text-sm sm:text-lg  text-neutral-600">
                    <span class="text-sm uppercase tracking-widest mb-3 opacity-70">CO<sub>2</sub> removal</span><br />
                    {{ formatCurrency(method.totals.co2_removal_total) }}
                </div>
            </div>

            <div class="mt-12 text-neutral-600 max-w-3xl mx-auto">
                As you can see, the lease and fuel costs of running a petrol vehicle with carbon removal can be slightly <b>less than the same costs of running an electric vehicle</b> over a 3-year period.
            </div>

            <div class="my-6 text-neutral-600 max-w-3xl mx-auto">
                Both options will produce <b>net-zero CO<sub>2</sub> emissions</b> over the period, as long as the electricity used to charge the electric vehicle is from a renewable source.
                Electric vehicles may have other benefits, such as <b>zero local emissions</b> and generally lower maintenance and tax costs.
            </div>

            <div class="my-8 pt-8 border-t mb-16 text-neutral-600 max-w-3xl mx-auto text-sm">
                <p>
                    Turncarbon's calculations are based on the following variables:
                </p>
                <ul class="mt-4">
                    <li>
                        The {{ example.methods[0].vehicle }} has a WLTP fuel consumption of {{ example.methods[0].wltp_100km }} {{ example.methods[0].fuel_unit}} per 100km, and a fuel tank capacity of {{ example.methods[0].storage }} {{ example.methods[0].fuel_unit }}.
                    </li>
                    <li>
                        The {{ example.methods[1].vehicle }} has a WLTP energy consumption of {{ example.methods[1].wltp_100km }} {{ example.methods[1].fuel_unit}} per 100km, and a battery capacity of {{ example.methods[1].storage }} {{ example.methods[1].fuel_unit }}.
                    </li>
                    <li>
                        The cost of {{ example.methods[0].fuel_name }} is {{ example.methods[0].fuel_price }}p per {{ example.methods[0].fuel_unit_singular }}. The cost of {{ example.methods[1].fuel_name }} is {{ example.methods[1].fuel_price }}p per {{ example.methods[1].fuel_unit_singular }}.
                    </li>
                    <li class="mt-3">
                        Lease costs were obtained from a popular leasing company on 5/3/2024. They are based on a 3-year lease with a 15,000 mile annual allowance and 1 month upfront initial payment.
                    </li>
                    <li class="mt-3">
                        The total includes the cost of the fuel, the cost of the lease, and the cost of the carbon removal. It does not include other costs such as insurance, maintenance, or road tax.
                    </li>
                </ul>
            </div>
        </div>

    </PublicLayout>
</template>
